import { CompactOrderResponse, OrderResponse, OrderStageId, OrderStatus } from '@business/entities/order';
import { OrgId, OrgResponse } from '@business/entities/organization';
import { CommonService, OrderService } from '@business/services';
import { Button } from '@core/components/Button';
import { FormSelect } from '@core/components/form';
import { GCColumn, GCTable } from '@core/components/GCTable';
import { useHistory } from '@core/router';
import { useBloc } from '@core/utils/bloc';
import { formControl, FormDataType, formGroup } from '@core/utils/form';
import { Formatter } from '@core/utils/formatter';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { useObservable } from '@core/utils/hooks/rxjs';
import { openOrderOverlay } from '@modules/orders/OrderOverlay';
import { openOrderCancelModal } from '@modules/orders/OrderOverlay/OrderCancelModal';
import React, { memo, useEffect, useMemo } from 'react';
import { Card, CardBody, CardTitle, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { OrdersBloc } from './OrdersBloc';

function defineOrderPageForm() {
  return formGroup({
    status: formControl<OrderStatus>({
      initialValue: OrderStatus.open,
    }),
    stage: formControl<OrderStageId>({
      placeholder: 'Select Stage',
    }),
  });
}
export type OrdersFilterData = FormDataType<typeof defineOrderPageForm>;

export interface OrdersProps {
  orgId: OrgId;
  organization: OrgResponse;
  isModal?: boolean;
}
export const Orders = memo((props: OrdersProps) => {
  const { orgId, isModal } = props;
  const history = useHistory();
  const form = useMemo(() => defineOrderPageForm(), []);
  const formValue = useObservable(form.value$);
  const bloc = useBloc(OrdersBloc, orgId);
  const orders = useObservable(bloc.filteredOrders$, []);
  const stages = useObservable(bloc.stages$, []);

  const statusItems = useMemo(() => CommonService.selectItems(OrderService.allStatuses(), i => OrderService.getStatusTitle(i)), []);
  const stageItems = useMemo(() => CommonService.selectItems(stages, 'name', 'id'), [stages]);

  useEffect(() => {
    if (formValue) {
      bloc.filters$.next(formValue);
    }
  }, [bloc, formValue]);

  const columns: GCColumn<CompactOrderResponse>[] = useMemo(
    () => [
      {
        id: 'orderId',
        name: `Order ID`,
        sortField: 'orderId',
        minWidth: '150px',
        sortable: true,
        cell: row => (
          <a className="ml-0" onClick={() => openOrderOverlay({ orderId: row.id })}>
            {row.orderNumber}
          </a>
        ),
      },
      {
        name: 'Stage',
        sortField: 'stage',
        cell: item => item.stage.name,
        minWidth: '80px',
      },
      {
        name: 'Balance',
        sortField: 'balance',
        cell: item => Formatter.formatCurrency(item.balance, 'USD'),
        minWidth: '80px',
      },
      {
        name: 'Date',
        sortField: 'date',
        cell: item => Formatter.formatTime(item.created),
        minWidth: '80px',
      },
      {
        name: '',
        sortField: 'action',
        width: '80px',
        cell: order => {
          return (
            <UncontrolledDropdown>
              <DropdownToggle tag="a" className="font-size-16 d-flex justify-content-center align-items-center text-dark">
                <i className="bx bx-dots-horizontal-rounded"></i>
              </DropdownToggle>

              <DropdownMenu container="body" className="dd-menu_outside">
                {order.canEdit && (
                  <a
                    className={`dropdown-item`}
                    onClick={() => {
                      history.replace(`/order/edit/${order.organization.id}/${order.id}`);
                    }}
                  >
                    Edit {gcLabel('Order')}
                  </a>
                )}

                {order.status !== OrderStatus.canceled && (
                  <a className="dropdown-item text-danger" onClick={() => openOrderCancelModal({ order: order as OrderResponse })}>
                    Cancel {gcLabel('Order')}
                  </a>
                )}
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [history],
  );

  return (
    <Card>
      <CardBody>
        <CardTitle className="d-flex align-items-center justify-content-between py-2">
          Orders
          <div className="d-flex">
            <div style={{ minWidth: 150 }}>
              <FormSelect withoutGroup control={form.controls.status} items={statusItems} />
            </div>
            <div className="ml-2" style={{ minWidth: 150 }}>
              <FormSelect withoutGroup control={form.controls.stage} items={stageItems} />
            </div>
            <Button
              extraClassNames="ml-4"
              intent="primary"
              onClick={() => {
                if (isModal) {
                  return (window.location.href = `/orders/create/${orgId}`);
                }

                history.push(`/orders/create/${orgId}`);
              }}
            >
              Add Order
            </Button>
          </div>
        </CardTitle>
        <GCTable columns={columns} data={orders} noSearch subHeader={false} />
      </CardBody>
    </Card>
  );
});
